import classNames from 'classnames/bind';
import PropTypes from 'prop-types';
import React from 'react';

import Agenda from 'components/Shared/Agenda';
import LoadingScreen from 'components/Shared/LoadingScreen';
import StudyDeadline from 'components/Shared/StudyDeadline';
import Button from 'uikit/Button';
import Icon from 'uikit/Icon/Icon';
import Menu, { MenuItem } from 'uikit/Menu';

import ClassificationMatrix from './ClassificationMatrix';
import CourseProgress from '../../Shared/Progress/CourseProgress';
import Chapters from '../../Shared/Progress/Chapters';
import Tags from './Tags';
import Questions from './Questions';
import Report from './Report';
import Students from './Students';
import styles from './Statistics.module.scss';

const cx = classNames.bind(styles);

const Statistics = ({
  className,
  data,
  display,
  course,
  groupInputValue,
  handleChangeType,
  handleGradeSynchronize,
  handleGroupInputBlur,
  handleGroupInputChange,
  handleGroupSelect,
  handleChangeDisplay,
  handleStudentInputBlur,
  handleStudentInputChange,
  handleStudentSelect,
  hasNoGroups,
  hasNoStudents,
  isLoading,
  studentInputValue,
  filteredGroups,
  filteredStudents,
  t,
  type,
}) =>
  isLoading ? (
    <LoadingScreen />
  ) : (
    <div className={cx('container', className)}>
      {course.moodle.isMoodle && (
        <Button
          className={cx('lms')}
          intent="primary"
          onClick={handleGradeSynchronize}
        >
          {t('Course/Teacher/Statistics/moodle-grade-sync')}
        </Button>
      )}
      <div className={cx('choices')}>
        <button
          className={cx('choice', {
            isActive: display === 'statistics',
          })}
          onClick={() => handleChangeDisplay('statistics')}
          type="button"
        >
          {t('Course/Teacher/Statistics/statistics')}
        </button>
        <button
          className={cx('choice', {
            isActive: display === 'report',
          })}
          onClick={() => handleChangeDisplay('report')}
          type="button"
        >
          {t('Course/Teacher/Statistics/report')}
        </button>
      </div>
      <div className={cx('header')}>
        <div className={cx('select-container')}>
          <select
            className={cx('select')}
            onChange={(evt) => handleChangeType(evt.target.value)}
            value={type}
          >
            <option value="full" disabled={hasNoStudents}>
              {t(
                display === 'statistics'
                  ? 'Course/Teacher/Statistics/stats-see-full'
                  : 'Course/Teacher/Statistics/report-see-full',
              )}
            </option>
            <option value="me">
              {t(
                display === 'statistics'
                  ? 'Course/Teacher/Statistics/stats-see-me'
                  : 'Course/Teacher/Statistics/report-see-me',
              )}
            </option>
            <option value="group" disabled={hasNoGroups}>
              {t(
                display === 'statistics'
                  ? 'Course/Teacher/Statistics/stats-see-groups'
                  : 'Course/Teacher/Statistics/report-see-groups',
              )}
            </option>
            <option value="student" disabled={hasNoStudents}>
              {t(
                display === 'statistics'
                  ? 'Course/Teacher/Statistics/stats-see-students'
                  : 'Course/Teacher/Statistics/report-see-students',
              )}
            </option>
          </select>
        </div>
        <div className={cx('filler')} />
        {type === 'group' && (
          <Menu
            className={cx('menu-group')}
            isHideOnClickDeactivated
            placement="bottom"
            toggle={
              <div className={cx('input-group-container')}>
                <input
                  className={cx('input-group')}
                  onBlur={handleGroupInputBlur}
                  onChange={handleGroupInputChange}
                  type="text"
                  value={groupInputValue}
                />
                <Icon className={cx('icon-input-group')} name="chevron-down" />
              </div>
            }
          >
            {filteredGroups.map((group) => (
              <MenuItem
                key={group.id}
                onClick={() => handleGroupSelect(group.id)}
                variant="regular"
              >
                {group.title}
              </MenuItem>
            ))}
          </Menu>
        )}
        {type === 'student' && (
          <Menu
            className={cx('menu-student')}
            isHideOnClickDeactivated
            placement="bottom"
            toggle={
              <div className={cx('input-student-container')}>
                <input
                  className={cx('input-student')}
                  onBlur={handleStudentInputBlur}
                  onChange={handleStudentInputChange}
                  type="text"
                  value={studentInputValue}
                />
                <Icon
                  className={cx('icon-input-student')}
                  name="chevron-down"
                />
              </div>
            }
          >
            {filteredStudents.map((student) => (
              <MenuItem
                key={student.id}
                onClick={() => handleStudentSelect(student.id)}
                variant="regular"
              >
                {`${student.lastName.toUpperCase()}, ${student.firstName}`}
              </MenuItem>
            ))}
          </Menu>
        )}
      </div>
      {display === 'statistics' ? (
        <>
          <CourseProgress className={cx('course')} course={data.course} />
          {type === 'me' &&
            (course.deadline ? (
              <Agenda course={data.course} isSingle />
            ) : (
              <StudyDeadline
                className={cx('study-deadline')}
                course={data.course}
                from="teacher-statistics"
              />
            ))}
          <ClassificationMatrix
            className={cx('matrix')}
            course={course}
            matrix={data.classificationMatrix}
          />
          {data.chapters.length !== 0 && (
            <Chapters
              className={cx('chapters')}
              chapters={data.chapters}
              course={data.course}
            />
          )}
          {data.tags.length !== 0 && (
            <Tags className={cx('tags')} tags={data.tags} />
          )}
          {data.questions.length !== 0 && (
            <Questions className={cx('questions')} questions={data.questions} />
          )}
          {data.students.length !== 0 && (
            <Students className={cx('students')} students={data.students} />
          )}
        </>
      ) : (
        <Report aggregatedStudyAnswers={data} />
      )}
    </div>
  );

Statistics.propTypes = {
  className: PropTypes.string,
  course: PropTypes.object,
  data: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.arrayOf(PropTypes.object),
  ]),
  display: PropTypes.oneOf(['statistics', 'report']).isRequired,
  groupInputValue: PropTypes.string.isRequired,
  handleChangeType: PropTypes.func.isRequired,
  handleGradeSynchronize: PropTypes.func.isRequired,
  handleGroupInputBlur: PropTypes.func.isRequired,
  handleGroupInputChange: PropTypes.func.isRequired,
  handleGroupSelect: PropTypes.func.isRequired,
  handleChangeDisplay: PropTypes.func.isRequired,
  handleStudentInputBlur: PropTypes.func.isRequired,
  handleStudentInputChange: PropTypes.func.isRequired,
  handleStudentSelect: PropTypes.func.isRequired,
  hasNoGroups: PropTypes.bool.isRequired,
  hasNoStudents: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  studentInputValue: PropTypes.string.isRequired,
  filteredGroups: PropTypes.arrayOf(PropTypes.object),
  filteredStudents: PropTypes.arrayOf(PropTypes.object),
  t: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
};

Statistics.defaultProps = {
  className: undefined,
  course: undefined,
  data: undefined,
  filteredGroups: [],
  filteredStudents: [],
};

export default Statistics;
